import { Controller } from '@hotwired/stimulus';

// Mono-VAT pre-fill: maps the contract's active-rate key (as emitted in
// `wps-form-contract-active-rates-value` JSON) to its numeric percentage.
// Only the three French rates are supported — any unknown key skips prefill.
const WPS_FORM_RATE_BY_KEY = Object.freeze({ '5_5': 5.5, 10: 10, 20: 20 });
// Keys are always coerced to strings by JS — explicit quoting on '5_5' is
// only needed because of the underscore. '10' / '20' lookups work either way.

/**
 * WPS Form Controller (multi-invoice flow on /work_progress_statements/new
 * and /edit via the InvoiceAttachPanelComponent — Phase D)
 *
 * Operates in two modes controlled by `modeValue`:
 *
 * **Buffered mode** (`'buffered'`, default — /new):
 *   - File selection via dropzone (click) and drag-and-drop
 *   - Row template cloning (one row per File OR one row per portal-attached doc)
 *   - Bulk + per-card attach of pending portal invoices (`attachPortal`,
 *     `attachAllPortal`) — synthesizes a row that carries `invoice_document_id`
 *     instead of a `File`, stamping the source pool card with
 *     `data-attach-state="attached"` (logical dedup) and `.hidden` (visual)
 *     so it can't be double-attached.
 *   - Removal + reindexing so submitted indices are 0..N-1
 *   - Hidden file inputs populated via DataTransfer at submit-time so the
 *     Rails controller receives `work_progress_statement[line_inputs][N][file]`
 *
 * **Live mode** (`'live'` — /edit):
 *   - Each file drop POSTs `invoice[file]=<file>` to `attachUrlValue` as
 *     multipart; the server responds with a Turbo Stream that appends the row.
 *   - Each "+ Attacher" click POSTs `invoice[invoice_document_id]=<id>` to
 *     the same endpoint, stamping the card with `data-attach-state` through
 *     the available → attaching → attached lifecycle so concurrent calls
 *     (per-card + bulk) cannot POST the same doc twice.
 *   - `lineMap` and the template-clone path are NOT used; `lineMap.size`
 *     stays 0 so the beforeunload guard falls back to `#formIsDirty()`.
 *   - The submit-block pending-guard still applies (rows with
 *     `data-row-status='pending'` are broadcast by the server).
 *
 * Shared behaviour (both modes):
 *   - Submit guard: if any row carries `data-row-status='pending'` (AI
 *     extraction in flight), block submit + flash a warning + focus the row.
 *   - Reconciliation guard: if any row has an unresolved sum mismatch
 *     (AI-extracted total ≠ per-rate inputs sum), block submit + flash a
 *     warning. Rate mismatches (unknown VAT rate) do NOT block submit.
 *   - Client-side MIME / size validation — files failing either bound never
 *     proceed; the architect sees an inline French flash.
 *   - Drag-active styling via a single `is-drag-active` class on
 *     `dragActiveTarget` — distinct from `:hover` so designers can
 *     differentiate the two states.
 *   - Beforeunload guard if the user has unsaved work.
 *   - Auto-attach a single pool card from `?invoice_document_id=<id>` (legacy
 *     URL contract).
 *
 * The actual `/invoice_analyses` POST is owned by the per-row child controller
 * (`invoice-line-uploader`); this parent only holds the canonical File state in
 * `lineMap` and exposes it via `getLineFile(localId)` for the child outlet.
 * Extraction results are fed back via `setLineExtractionReference(localId, payload)`
 * so reconciliation can run client-side.
 *
 * Dropzone integration:
 *   The dropzone area is rendered by `Form::DropzoneComponent` which mounts its
 *   own `file-dropzone` Stimulus controller. That controller owns drag, drop,
 *   keyboard activation, single-file preview, and per-file validation; it
 *   dispatches `file-dropzone:selected` (bubbling, detail `{ files | file }`)
 *   when files are accepted. This controller subscribes to that event via
 *   `data-action="file-dropzone:selected->wps-form#onFileDropzoneSelected"` on
 *   the form and pipes the files into `#processSelectedFiles`. No file input
 *   target is owned here.
 *
 * Targets:
 *   - linesTbody        : <tbody> of #invoice-lines-table where rows are appended
 *   - linesTable        : <table> wrapping the lines (toggled visible w/ rows)
 *   - lineTemplate      : <template> containing the row HTML to clone
 *   - emptyState        : empty-state element (toggled hidden when rows exist)
 *   - dragActive        : element to receive `is-drag-active` on drag (Phase B
 *                         panel container) — optional; file-dropzone owns its
 *                         own visual feedback on the label, dragActive is only
 *                         used by the outer panel for cross-cutting styling.
 *   - submitWarning     : container for the inline flash (queried lazily; the
 *                         panel doesn't render this yet — created on first use).
 *   - reviewAlert       : top-of-form alert shown when ≥1 unresolved issue exists.
 *   - reviewAlertCount  : <span> inside reviewAlert holding the issue count.
 *   - submitButton      : the form's primary submit button; disabled when sum
 *                         mismatches are unresolved.
 *
 * Values:
 *   - mode                          : 'buffered' (/new) or 'live' (/edit) — Phase D.
 *   - attachUrl                     : POST /invoices URL for :live mode (Phase D).
 *   - csrfToken                     : Read once for fetch headers (Phase D).
 *   - maxFileSizeMb                 : Client-side size cap (default 10 Mo).
 *   - acceptedMimes                 : Allow-list of MIME types (default PDF/JPEG/PNG).
 *   - autoAttachInvoiceDocumentId   : Pool card to auto-attach on connect.
 *   - contractActiveRates           : JSON array of rate strings active on the
 *                                     contract, e.g. '["10","20"]'. Used for
 *                                     rate-mismatch detection against AI output.
 *
 * Actions:
 *   - onFileDropzoneSelected : `file-dropzone:selected` -> on the form. Reads
 *                              `event.detail.files` (multi) or `event.detail.file`
 *                              (single) and pipes through `#processSelectedFiles`.
 *   - attachPortal      : click on a pool card's "+ Attacher" button
 *   - attachAllPortal   : click on the panel-header "Tout attacher" bulk button
 *   - removeLine        : click on a row's delete button
 *   - beforeSubmit      : submit on the form (pending-guard + reconciliation-guard
 *                         + populates file inputs)
 *   - onLineInput       : input event bubbling from any line-row input; triggers
 *                         reconciliation recompute for the changed row.
 *   - recalcTotal       : click on strip's "Recalculer le total" button; sums
 *                         per-rate inputs and writes result to amount_ht.
 *   - dismissRateMismatch : click on strip's "Compris" button; dismisses the
 *                           rate-mismatch strip for a line.
 *
 * Dispatched events (all `wps-form:`-prefixed via Stimulus convention):
 *   - line-added            : after a row is appended
 *   - line-removed          : before a row is removed (so the child can abort)
 *   - attach-state-changed  : after attach/attachAll mutate `lineMap`
 *
 * lineMap entry shape (after reconciliation extension):
 * ```
 * {
 *   file?: File,                         // buffered file upload
 *   invoiceDocumentId?: number,          // portal-attached doc
 *   referenceAmountHt?: number,          // extracted_data.amount_ht from AI
 *   referenceVatBreakdown?: Array<{      // extracted_data.vat_breakdown from AI
 *     rate: number|null,
 *     amount_ht: number,
 *     amount_vat: number
 *   }>,
 *   rateMismatchDismissed?: boolean,     // true after architect clicks "Compris"
 * }
 * ```
 *
 * @example
 *   <form data-controller="wps-form"
 *         data-action="file-dropzone:selected->wps-form#onFileDropzoneSelected
 *                      submit->wps-form#beforeSubmit
 *                      input->wps-form#onLineInput"
 *         data-wps-form-mode-value="buffered"
 *         data-wps-form-max-file-size-mb-value="10"
 *         data-wps-form-accepted-mimes-value='["application/pdf","image/jpeg","image/png"]'
 *         data-wps-form-contract-active-rates-value='["10","20"]'>
 *     <div data-wps-form-target="reviewAlert" data-wps-form-issue-count="0" hidden>
 *       <span data-wps-form-target="reviewAlertCount">0</span>
 *     </div>
 *     <%# DropzoneComponent renders its own data-controller="file-dropzone" wrapper %>
 *     <%= render Form::DropzoneComponent.new(form: f, field: :invoice_files, multiple: true,
 *           accept: ".pdf,.jpg,.jpeg,.png", max_size: 10) %>
 *     <table>
 *       <tbody data-wps-form-target="linesTbody"></tbody>
 *     </table>
 *     <template data-wps-form-target="lineTemplate">…</template>
 *     <div data-wps-form-target="emptyState">Aucune facture</div>
 *     <button type="submit" data-wps-form-target="submitButton">Enregistrer</button>
 *   </form>
 */
export default class extends Controller {
  static targets = [
    'linesTbody',
    'linesTable',
    'lineTemplate',
    'emptyState',
    'dragActive',
    'submitWarning',
    'amountField',
    'amountReadonlyHelper',
    'poolWrapper',
    'poolBadge',
    'bulkAttachButton',
    'reviewAlert',
    'reviewAlertCount',
    'submitButton',
    'statementTypeSelect',
    'vatAmountInput',
    'lineTotalHt',
    // WPS-level TVA card root — toggled hidden whenever ≥1 invoice row exists.
    // Card visible iff no rows (acompte / pre-attach path); the lines-table
    // footer is the single source of truth once invoices are attached.
    'vatCard',
    // Per-row TTC display cell. Live-computed from `HT + Σ vat_amount_<rate>`
    // by `#computeRowTtc` whenever the architect edits an HT/TVA input.
    // The footer's `ttcTotalCell` (different controller, same split) is the
    // sum across rows. Server pre-renders both so initial paint has no flicker.
    'ttcCell',
  ];

  static outlets = ['invoice-lines-table'];

  static values = {
    mode: { type: String, default: 'buffered' },
    attachUrl: String,
    csrfToken: String,
    maxFileSizeMb: { type: Number, default: 10 },
    acceptedMimes: {
      type: Array,
      default: ['application/pdf', 'image/jpeg', 'image/png'],
    },
    autoAttachInvoiceDocumentId: Number,
    contractActiveRates: { type: Array, default: [] },
    vatExempt: { type: Boolean, default: false },
    selectTypeLabel: { type: String, default: 'Sélectionnez un type de situation' },
  };

  connect() {
    /** @type {Map<string, { file?: File, invoiceDocumentId?: number }>} localId → entry */
    this.lineMap = new Map();

    // Prime the submit button so /new lands disabled with the correct
    // reason (typically "missing statement_type" for a fresh form).
    this.#updateSubmitButton();

    // Snapshot the form's initial state so the beforeunload guard only fires
    // when the user has actually edited something.
    this.initialFormSnapshot = this.#snapshotForm();

    this.boundBeforeUnload = this.beforeunload.bind(this);
    window.addEventListener('beforeunload', this.boundBeforeUnload);

    // Listen for child-controller pill state changes so beforeSubmit always
    // sees the freshest data-row-status (mirrored by invoice-line-uploader
    // when its #setPillState runs — Phase A wired the dispatch).
    this.boundRowStateChanged = this.#onRowStateChanged.bind(this);
    this.element.addEventListener('wps-form:row-state-changed', this.boundRowStateChanged);

    // Stamp `dataset.userDirty='true'` on the first keydown of any HT or TVA
    // input. The AI-extraction handler in `invoice-line-uploader` checks this
    // attribute before writing a value, so a late extraction can't overwrite
    // an architect's typing once they've started — even if the row exits
    // `pending` after the architect's first keystroke.
    this.boundUserDirtyHandler = this.#onUserKeydown.bind(this);
    this.element.addEventListener('keydown', this.boundUserDirtyHandler);

    // Auto-attach the legacy URL prefill (controller sets this on /new
    // when ?invoice_document_id=<id> resolves to a portal-pending doc).
    if (this.hasAutoAttachInvoiceDocumentIdValue && this.autoAttachInvoiceDocumentIdValue > 0) {
      // Defer one tick so the panel's pool cards are connected.
      queueMicrotask(() => this.#autoAttachFromUrl());
    }

    // Surface VAT coherence advisories for server-rendered rows that already
    // carry values (e.g. /edit load). They emit no `input` events, so #onLineInput
    // never fires for them — sweep once after connect so a saved incoherent line
    // shows its "TVA à vérifier" strip on arrival, not only after an edit.
    // `this.element`-scoped (NOT #rowElements(), which is empty in live/edit mode
    // where there is no linesTbody target) so /edit rows are covered too.
    queueMicrotask(() => {
      this.element.querySelectorAll('[data-local-id]').forEach((row) => {
        const localId = row.dataset.localId;
        if (localId) this.#recomputeVatCoherence(localId);
      });
    });
  }

  disconnect() {
    window.removeEventListener('beforeunload', this.boundBeforeUnload);
    this.element.removeEventListener('wps-form:row-state-changed', this.boundRowStateChanged);
    this.element.removeEventListener('keydown', this.boundUserDirtyHandler);
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  /**
   * `file-dropzone:selected` event handler. Files came from the child
   * `file-dropzone` controller after it already validated size + MIME on its
   * side. We re-validate here against `acceptedMimesValue` + `maxFileSizeMbValue`
   * (defense in depth — those values may be tighter than file-dropzone's own)
   * and route through `#processSelectedFiles`.
   */
  onFileDropzoneSelected(event) {
    const detail = event.detail || {};
    const files = detail.files || (detail.file ? [detail.file] : []);
    if (files.length === 0) return;

    this.#processSelectedFiles(Array.from(files));
  }

  /**
   * Per-card attach: triggered by a pool card's "+ Attacher" button.
   *
   * In buffered mode: synthesizes a row carrying `invoice_document_id` (no
   * File), removes the source card, and dispatches `wps-form:attach-state-changed`.
   *
   * In live mode: POSTs `invoice[invoice_document_id]` to `attachUrlValue`
   * as multipart and lets the Turbo Stream response append the new row
   * server-side. The card is hidden optimistically and removed on success.
   */
  attachPortal(event) {
    event?.preventDefault();
    const card = event.target.closest('[data-testid="pending-pool-card"]');
    if (!card) return;

    if (this.modeValue === 'live') {
      this.#liveAttachPortalCard(card);
    } else {
      this.#attachPortalCard(card);
      this.dispatch('attach-state-changed', {
        detail: { lineMapSize: this.lineMap.size },
      });
    }
  }

  /**
   * Bulk attach from the panel header "Tout attacher" button — rendered by
   * InvoiceAttachPanelComponent when the pool has ≥ 2 cards. Iterates every
   * visible pool card and attaches each in turn.
   *
   * In buffered mode: attaches each card synchronously via the buffered path
   * and emits a single `wps-form:attach-state-changed`.
   *
   * In live mode: fires an async POST per card (each call is independent;
   * errors surface as per-card flash messages).
   */
  attachAllPortal(event) {
    event?.preventDefault();
    // Skip cards already attached or mid-attach. The helper-level guards in
    // #attachPortalCard / #liveAttachPortalCard would catch them, but
    // pre-filtering avoids spinning over a known no-op set.
    const cards = document.querySelectorAll(
      '[data-testid="pending-pool-card"][data-attach-state="available"]'
    );
    if (this.modeValue === 'live') {
      cards.forEach((card) => this.#liveAttachPortalCard(card));
    } else {
      cards.forEach((card) => this.#attachPortalCard(card));
    }
    this.dispatch('attach-state-changed', {
      detail: { lineMapSize: this.lineMap.size, bulk: true, mode: this.modeValue },
    });
  }

  removeLine(event) {
    event.preventDefault();
    const row = event.currentTarget.closest('[data-local-id]');
    if (!row) return;

    const localId = row.dataset.localId;

    // Notify the child controller so it can abort any in-flight fetch BEFORE we
    // remove its element from the DOM (disconnect() also fires automatically,
    // but dispatching a removed event keeps the contract explicit).
    this.dispatch('line-removed', { detail: { localId }, target: row });

    // Restore the source pool card if this row was created by attaching one.
    // Buffered mode only — live-mode restore is handled server-side by the
    // second Turbo Stream emitted from InvoicesController#destroy, which
    // replaces #pending-pool-wrapper with the refreshed pool partial.
    const portalDocId = row.dataset.fromPortalDocId;
    if (portalDocId) {
      const card = document.querySelector(
        `[data-testid="pending-pool-card"][data-doc-id="${portalDocId}"]`
      );
      if (card) {
        card.classList.remove('hidden');
        card.dataset.attachState = 'available';
      }
    }

    this.lineMap.delete(localId);
    row.remove();
    // The line's warning strips are SIBLING <tr>s (keyed by data-line-id), not
    // children, so row.remove() would orphan any visible one (a stale "TVA à
    // vérifier" stranded over an unrelated row). Tear them down with the row.
    this.#removeStripsFor(localId);
    // The removed line may have owned a banner-counted strip (sum/rate); recount
    // so the top-of-form "à vérifier" alert doesn't keep a stale figure for a
    // warning that no longer exists (vat_coherence is excluded from the banner).
    this.#updateReviewAlert();
    this.reindexRows();
    this.#refreshEmptyState();
    this.#updatePoolVisibility();

    // The MutationObserver in invoice-lines-table fires on row.remove() but
    // its parseTotal() reads the stale tfoot textContent — the recompute
    // methods are wired to `input` events, which row removal doesn't fire.
    // Trigger a fresh recompute now so Total HT, per-rate cells, footer TTC,
    // period_amount_ht input (via onTotalChanged → readonly mirror), and the
    // per-row lineTotalHt spans all reflect the new state.
    //
    // Order matters: recompute() must run BEFORE updateAmountFieldMode().
    // recompute() dispatches `invoice-lines:total-changed`; amount-validator's
    // listener early-returns when the amount field is editable, so flipping
    // the mode FIRST causes a stale overrun errorBox to persist after the last
    // row is removed (QA scenario 3). Recompute first → validator sees the
    // field still readonly → re-evaluates with totalHt=0 → clears errorBox.
    this.invoiceLinesTableOutlet?.recompute();
    this.updateAmountFieldMode();
    this.recomputeAggregates();
  }

  beforeSubmit(event) {
    // ── Submit-block guard (P1-9) ────────────────────────────────────────────
    // If any row is still mid-extraction (`data-row-status='pending'`),
    // block the submit, focus the offending row, and flash a French message.
    const pendingRow = this.#firstPendingRow();
    if (pendingRow) {
      event.preventDefault();
      event.stopPropagation();
      this.#focusRow(pendingRow);
      this.#showFlash(
        "Une extraction IA est en cours. Patientez ou utilisez « Réessayer » avant d'enregistrer.",
        'warning'
      );
      return;
    }

    // ── Contract-total overrun gate — Enter-key safety net ───────────────────
    // amount-validator already disables the button on overrun, but Enter inside
    // any text input still triggers `submit`. Read the validator's public state
    // via the Stimulus application registry (both controllers share this.element).
    const amountValidator = this.application.getControllerForElementAndIdentifier(
      this.element,
      'amount-validator'
    );
    if (amountValidator?.isOverrun?.()) {
      event.preventDefault();
      event.stopPropagation();
      this.#showFlash(
        'Le cumul HT dépasse le total du contrat. Réduisez ou retirez une facture.',
        'warning'
      );
      // Two errorBox slots (legacy + multi) live in the DOM; only one is shown
      // at a time (driven by `data-mode-scope`). Scroll the visible one.
      const visible = amountValidator.visibleErrorBox?.();
      visible?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      visible?.focus();
      return;
    }

    // ── Reconciliation gate ──────────────────────────────────────────────────
    // Re-run reconciliation for all rows before submit to catch any
    // sum-mismatch that appeared after the last input event.
    this.#rowElements().forEach((row) => {
      const localId = row.dataset.localId;
      if (localId) this.#recomputeReconciliation(localId);
    });

    if (this.#hasUnresolvedSumMismatch()) {
      event.preventDefault();
      event.stopPropagation();
      this.#showFlash('Vérifiez les lignes en surbrillance avant validation.', 'warning');
      return;
    }

    // ── Existing file-binding logic (preserved verbatim) ─────────────────────
    // No-op on rows that already reference a portal-prefilled invoice_document_id.
    // For each in-memory file row, ensure the hidden file input exists in the
    // row and is populated via DataTransfer.
    const rows = this.#rowElements();
    rows.forEach((row, index) => {
      this.#applyIndexToRow(row, index);

      const localId = row.dataset.localId;
      const entry = this.lineMap.get(localId);
      const hasInvoiceDocumentId = !!row.querySelector(
        `input[name="work_progress_statement[line_inputs][${index}][invoice_document_id]"]`
      );

      if (hasInvoiceDocumentId) return; // portal-prefilled, no file to attach

      if (!entry || !entry.file) return; // defensive — should not happen for non-portal rows

      this.#ensureFileInput(row, index, entry.file);
    });

    // Disable the beforeunload guard now that the form is submitting.
    window.removeEventListener('beforeunload', this.boundBeforeUnload);
  }

  beforeunload(event) {
    if (this.lineMap.size === 0 && !this.#formIsDirty()) return;

    // Modern browsers ignore custom strings — they show their own confirm
    // dialog — but setting returnValue is still required to trigger it.
    event.preventDefault();
    event.returnValue = 'Vous avez des modifications non sauvegardées. Quitter quand même ?';
    return event.returnValue;
  }

  /**
   * Switch the "Montant HT de la situation" input between editable and readonly
   * modes based on the current row count (amélioration #8). Called from
   * `addLine`, `removeLine`, `attachPortal`, `attachAllPortal`, the live attach
   * paths (`#liveAttachPortalCard`, `#liveUploadFile`) and `#attachPortalCard`.
   *
   * Editable (0 rows) — input remains a normal `period_amount_ht_input`, the
   *   architect can saisir un montant manuel (cas acompte).
   * Readonly (≥1 row) — input gets `readonly` + `data-amount-mode="readonly"`,
   *   the helper "Calculé depuis les factures ci-dessous" shows up. The actual
   *   amount comes from the `invoice-lines:total-changed` event detail.
   */
  updateAmountFieldMode() {
    const hasRows = this.#rowElements().length > 0;
    this.#applyAmountMode(hasRows);
    this.#syncVatCardVisibility(hasRows);
  }

  /**
   * Listener for `invoice-lines:total-changed` — when the table dispatches a
   * new total HT, mirror it into the readonly input value so the architect
   * sees the derived sum directly in the field (instead of `0,00`). Idempotent
   * with `updateAmountFieldMode` — both can run in any order.
   */
  onTotalChanged(event) {
    // Live mode (/edit): rows arrive via Turbo Stream — there is no
    // `linesTbody` target in the wps-form scope, so updateAmountFieldMode()
    // would see 0 rows. Fall back to the count provided by invoice-lines-table
    // and apply both the amount-field mode AND the VAT card visibility.
    const eventCount = event?.detail?.count;
    if (typeof eventCount === 'number' && this.modeValue === 'live') {
      this.#applyAmountMode(eventCount > 0);
      this.#syncVatCardVisibility(eventCount > 0);
    }

    if (!this.hasAmountFieldTarget) return;
    if (this.amountFieldTarget.dataset.amountMode !== 'readonly') return;

    const totalHt = event?.detail?.totalHt;
    if (typeof totalHt === 'number' && Number.isFinite(totalHt)) {
      this.amountFieldTarget.value = totalHt.toFixed(2);
    }
  }

  /**
   * Listener for the statement_type SelectComponent's `change` event. Re-runs
   * the submit-button aggregator so an empty selection blocks submit and a
   * filled one (combined with no other blockers) re-enables it.
   */
  onStatementTypeChange() {
    this.#updateSubmitButton();
  }

  /**
   * Listener for `amount-validator:overrun-changed@window`. The amount validator
   * announces ceiling-overrun state changes; `wps-form` owns the submit button
   * so we re-run the aggregator. The `_event` payload is ignored — `#updateSubmitButton`
   * reads the validator's public `isOverrun()` directly to stay tolerant of
   * out-of-order events.
   */
  onOverrunChanged(_event) {
    this.#updateSubmitButton();
  }

  // ── Public helpers (called by the child uploader via outlet) ──────────────

  /**
   * Returns the File for a given localId, or null if the row has been removed
   * or references a portal document instead of an in-memory file.
   *
   * @param {string} localId
   * @returns {File|null}
   */
  getLineFile(localId) {
    const entry = this.lineMap.get(localId);
    return entry?.file || null;
  }

  /**
   * Store the AI extraction reference data for a given line. Called by the
   * per-row `invoice-line-uploader` child controller (via outlet) after the
   * `/invoice_analyses` response arrives.
   *
   * Extends the `lineMap` entry with:
   *   - `referenceAmountHt`      — `extracted_data.amount_ht` (numeric)
   *   - `referenceVatBreakdown`  — `extracted_data.vat_breakdown` (array)
   *
   * After storing, runs `#recomputeReconciliation` so the strip visibility and
   * top-of-form alert are updated immediately on extraction completion.
   *
   * @param {string} localId
   * @param {object} extractedData  — raw `extracted_data` payload from the server
   */
  setLineExtractionReference(localId, extractedData) {
    const entry = this.lineMap.get(localId);
    if (!entry) return;

    const amountHt = extractedData?.amount_ht;
    const breakdown = extractedData?.vat_breakdown;

    entry.referenceAmountHt =
      typeof amountHt === 'number' && Number.isFinite(amountHt) ? amountHt : null;
    entry.referenceVatBreakdown = Array.isArray(breakdown) ? breakdown : [];

    this.#recomputeReconciliation(localId);
  }

  // ── Reconciliation actions ────────────────────────────────────────────────

  /**
   * "Recalculer le total" strip action.
   *
   * Sums per-rate amount inputs in the row and writes the result to the row's
   * `amount_ht` input. Updates `referenceAmountHt` in `lineMap` so subsequent
   * input changes compare against the freshly-computed value, not the original
   * AI total. Re-runs reconciliation — the sum-mismatch strip should disappear
   * because the sum now equals the reference exactly.
   *
   * Dispatches a synthetic `input` event on the amount_ht input so
   * `invoice-lines-table` recalculates the footer total.
   *
   * @param {Event} event  — Stimulus action event with `params.lineId`
   */
  recalcTotal(event) {
    const localId = event.params.lineId;
    if (!localId) return;

    const row = this.#rowByLocalId(localId);
    if (!row) return;

    const sum = this.#sumPerRateInputs(row);

    const amountInput = row.querySelector('input[name$="[amount_ht]"]');
    if (amountInput) {
      // Mono-VAT: write back to amount_ht and let invoice-lines-table update
      // the footer via the synthetic input event.
      amountInput.value = sum.toFixed(2);
      amountInput.dispatchEvent(new Event('input', { bubbles: true }));
    } else {
      // Multi-VAT: no amount_ht input — update the lineTotalHt display span
      // and the WPS card aggregates.
      this.#updateLineTotalHtSpan(row);
      this.recomputeAggregates();
    }

    // Update the reference so the line is no longer in sum_mismatch.
    const entry = this.lineMap.get(localId);
    if (entry) {
      entry.referenceAmountHt = sum;
    }

    this.#recomputeReconciliation(localId);
  }

  /**
   * "Compris" strip action — dismisses the rate-mismatch warning for a line.
   *
   * Buffered mode (/new): persists the dismissal by injecting (or updating) a
   *   hidden input `work_progress_statement[line_inputs][N][rate_mismatch_dismissed_at]`
   *   with the current ISO timestamp so the Rails form object picks it up on submit.
   *
   * Live mode (/edit): PATCHes `/…/invoices/:invoice_id` with
   *   `{ invoice: { rate_mismatch_dismissed_at: <ISO> } }` using the same
   *   per-cell PATCH pattern as `invoice-line-row` controller. The row must
   *   carry `data-invoice-id` (server-rendered).
   *
   * @param {Event} event  — Stimulus action event with `params.lineId`
   */
  dismissRateMismatch(event) {
    const localId = event.params.lineId;
    if (!localId) return;

    const entry = this.lineMap.get(localId);
    if (entry) {
      entry.rateMismatchDismissed = true;
    }

    const row = this.#rowByLocalId(localId);
    const isoNow = new Date().toISOString();

    if (this.modeValue === 'live') {
      this.#patchRateMismatchDismissed(row, isoNow);
    } else {
      this.#injectRateMismatchDismissedInput(row, isoNow);
    }

    // Hide the rate-mismatch strip immediately (don't wait for server round-trip).
    this.#setStripVisible(localId, 'rate', false);
    this.#updateReviewAlert();
    this.#updateSubmitButton();
  }

  /**
   * `input` event listener on the form root — triggered whenever any input
   * inside a line row changes. Finds the row's `data-local-id` and re-runs
   * reconciliation for that line, plus the per-row TTC live-compute and
   * (when the changed input is `amount_ht` on a mono-VAT contract) the
   * one-shot TVA pre-fill.
   *
   * Wired via `data-action="input->wps-form#onLineInput"` on the `<form>` (or
   * on the panel container). Ignores events that originate outside a `[data-local-id]`
   * row (e.g. the period amount HT field above the table).
   *
   * @param {Event} event
   */
  onLineInput(event) {
    const row = event.target.closest('[data-local-id]');
    if (!row) return;

    const target = event.target;
    const localId = row.dataset.localId;
    if (localId) {
      this.#recomputeReconciliation(localId);
      this.#recomputeVatSanity(localId);
      this.#recomputeVatCoherence(localId);
    }
    this.recomputeAggregates();

    // Mono-VAT HT→TVA pre-fill: editing HT auto-fills TVA at the contract's
    // active rate. The sync dispatches a synthetic `input` event on the TVA
    // input so its own listeners (recompute, ttc) re-run; we gate routing on
    // `event.isTrusted` so the synthetic event doesn't re-enter the pre-fill.
    // ADR-009 Stage 1: per-line VAT is transcribed verbatim from the supplier
    // invoice — no TVA→HT reconciliation (would mask typos and re-hide the
    // `vat_total_within_realistic_range` sanity strip).
    if (event.isTrusted) {
      const isHtInput =
        target?.tagName === 'INPUT' &&
        typeof target.name === 'string' &&
        target.name.endsWith('[amount_ht]');

      if (isHtInput) {
        this.#syncMonoVatTvaFromHt(row, target);
      }
    }

    // Per-row TTC display = HT + Σ vat_amount_<rate>. The footer total TTC
    // lives in `invoice-lines-table#computeFooterTtc` (different controller,
    // intentional split — see that file's class-level comment).
    this.#computeRowTtc(row);
  }

  /**
   * Compute and write `HT + Σ vat_amount_<rate>` into the row's `ttcCell`
   * target. The server pre-renders the correct initial value via
   * `ttc_display` / `ttc_or_zero`; this method takes over on user edits.
   *
   * @param {HTMLElement} row
   */
  #computeRowTtc(row) {
    const ttcCell = row.querySelector('[data-wps-form-target="ttcCell"]');
    if (!ttcCell) return;

    // Mono-VAT buffered/live rows have an editable `[amount_ht]` input;
    // multi-VAT buffered rows surface HT only via the `lineTotalHt` span
    // (back-calculated from per-rate TVA in `recomputeMultiVatTotals`).
    // Read the input first; fall back to the span's text.
    const htInput = row.querySelector('input[name$="[amount_ht]"]');
    let ht = parseFloat(htInput?.value) || 0;
    if (!ht) {
      const lineTotalHtSpan = row.querySelector('[data-wps-form-target="lineTotalHt"]');
      if (lineTotalHtSpan) {
        ht = this.#parseLocaleNumber(lineTotalHtSpan.textContent);
      }
    }

    let vat = 0;
    row.querySelectorAll('input[name*="vat_amount_"]').forEach((input) => {
      vat += parseFloat(input.value) || 0;
    });

    ttcCell.textContent = this.#formatCurrency(ht + vat);
  }

  /**
   * Parse a French-locale currency / number string ("1 400,00 €" → 1400).
   * Mirrors the parser in `invoice_lines_table_controller#computeFooterTtc`.
   */
  #parseLocaleNumber(text) {
    if (!text) return 0;
    const cleaned = text
      .replace(/[^\d,.-]/g, '')
      .replace(/\s/g, '')
      .replace(',', '.');
    return parseFloat(cleaned) || 0;
  }

  /**
   * Mono-VAT only — HT edit recomputes the single active rate's TVA as
   * `HT × rate / 100`. Always overwrites the TVA input, regardless of
   * `data-user-dirty`: the architect's last-typed field is the anchor and
   * the other side auto-syncs. (Multi-VAT has no inverse rule and is skipped
   * here; the architect manages per-rate splits manually — per ADR-009
   * Stage 1, no client-side coherence check, just verbatim transcription.)
   *
   * Dispatches a synthetic `input` event on the TVA input so downstream
   * listeners refresh; `#onLineInput` blocks sync re-entry via `event.isTrusted`.
   */
  #syncMonoVatTvaFromHt(row, htInput) {
    const activeRates = this.contractActiveRatesValue;
    if (!Array.isArray(activeRates) || activeRates.length !== 1) return;

    const rateKey = activeRates[0];
    const ratePct = WPS_FORM_RATE_BY_KEY[rateKey];
    if (!ratePct) return;

    const vatInput = row.querySelector(`input[name$="[vat_amount_${rateKey}]"]`);
    if (!vatInput) return;

    const ht = parseFloat(htInput.value);
    if (!Number.isFinite(ht)) return;

    vatInput.value = ((ht * ratePct) / 100).toFixed(2);
    vatInput.dispatchEvent(new Event('input', { bubbles: true }));
  }

  /**
   * Refresh per-row `lineTotalHt` display spans for buffered (multi-VAT) rows.
   *
   * The Phase 6 refactor removed the WPS-card per-rate sum half: the card is
   * now a single visibility toggle and the lines-table footer owns all
   * per-rate totals via `invoice-lines-table`.
   */
  recomputeAggregates() {
    // Per-row span writes — INSIDE invoice-lines-table's MutationObserver scope.
    // Only safe when triggered by a user-input or row-mutation path that does
    // NOT re-enter via `invoice-lines:total-changed`. Never call from
    // `onTotalChanged` — that's the observer-driven path.
    this.element.querySelectorAll('[data-local-id]').forEach((row) => {
      this.#updateLineTotalHtSpan(row);
    });
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  /**
   * Clone the row template, populate placeholders, append to tbody, register
   * the file in `lineMap`, and dispatch a `wps-form:line-added` event so the
   * child controller can pick up the file for analysis.
   *
   * @param {File} file
   */
  addLine(file) {
    if (!this.hasLineTemplateTarget || !this.hasLinesTbodyTarget) return;

    const localId = this.#generateLocalId();
    const index = this.#rowElements().length;

    const fragment = this.lineTemplateTarget.content.cloneNode(true);

    // Placeholder substitution. The template contains literal `__LOCAL_ID__`
    // and `__INDEX__` tokens in attribute values; Stimulus only initializes
    // controllers once the cloned nodes are attached, so we mutate first.
    this.#replacePlaceholdersInFragment(fragment, localId, index);

    // Capture the form <tr> from the fragment BEFORE appendChild — the
    // template emits 4 sibling <tr>s (form row + 2 reconciliation strips
    // + 1 sanity strip), so `tbody.lastElementChild` would point at the
    // last strip and `target` would be the wrong row for the dispatched event.
    const row = fragment.querySelector('tr[data-testid="invoice-line-form-row"]');
    this.linesTbodyTarget.appendChild(fragment);

    // Pre-fill the row's "Nom de la facture" input with the file's name so the
    // architect sees the same value the server-side fallback would persist
    // (CreateService → file.original_filename). Mirrors the portal path's
    // #preFillPortalRowFields. Don't clobber an existing value (defensive —
    // the template currently always emits an empty value).
    if (row && file?.name) {
      const customNameInput = row.querySelector('input[name$="[custom_name]"]');
      if (customNameInput && !customNameInput.value) {
        customNameInput.value = file.name;
      }
    }

    this.lineMap.set(localId, { file });
    this.#refreshEmptyState();
    this.updateAmountFieldMode();

    if (row) {
      this.dispatch('line-added', { detail: { localId }, target: row });
    }
  }

  /**
   * Walk every row, update its index-bearing attributes (input names,
   * data-* attrs) so submitted indices are 0..N-1 with no gaps.
   */
  reindexRows() {
    this.#rowElements().forEach((row, index) => {
      this.#applyIndexToRow(row, index);
    });
  }

  // — Private (#-prefixed) helpers ——————————————————————————————————————————

  /**
   * Apply amount field mode for both buffered and live branches.
   * Mirrors the exact mutations of `updateAmountFieldMode` so required/readOnly
   * stay in sync regardless of which code path triggers the change.
   *
   * @param {boolean} hasRows
   */
  #applyAmountMode(hasRows) {
    if (!this.hasAmountFieldTarget) return;
    const input = this.amountFieldTarget;
    const wasReadonly = input.dataset.amountMode === 'readonly';
    if (hasRows) {
      input.setAttribute('data-amount-mode', 'readonly');
      input.setAttribute('readonly', 'readonly');
      input.removeAttribute('required');
    } else {
      input.setAttribute('data-amount-mode', 'editable');
      input.removeAttribute('readonly');
      input.setAttribute('required', 'required');
      // Transition readonly → editable (last line removed): the previous value
      // was a derived sum from now-removed invoices. Clear it so the architect
      // starts from a clean editable state instead of seeing the stale total.
      if (wasReadonly) {
        input.value = '';
      }
    }
    if (this.hasAmountReadonlyHelperTarget) {
      this.amountReadonlyHelperTarget.classList.toggle('hidden', !hasRows);
    }
  }

  /**
   * Toggle the WPS-level TVA card visibility based on invoice presence.
   *
   * Card visible iff no rows (acompte / pre-attach path). Once the architect
   * attaches the first invoice the lines-table footer becomes the single
   * source of truth and the card is hidden; detaching the last invoice
   * un-hides it.
   *
   * @param {boolean} hasRows
   */
  #syncVatCardVisibility(hasRows) {
    if (!this.hasVatCardTarget) return;
    if (this.vatExemptValue) {
      this.vatCardTarget.classList.add('hidden');
      return;
    }
    this.vatCardTarget.classList.toggle('hidden', hasRows);
  }

  /**
   * Funnel for `onFileDropzoneSelected`: validate each File
   * against `acceptedMimesValue` + `maxFileSizeMbValue`, surface rejections
   * as a single inline flash, then process the survivors.
   *
   * In buffered mode: survivors are added via `addLine` (client-side row clone).
   * In live mode: survivors are POSTed individually to `attachUrlValue`.
   */
  #processSelectedFiles(files) {
    const accepted = [];
    const rejected = [];

    files.forEach((file) => {
      const reason = this.#validateFile(file);
      if (reason) {
        rejected.push({ file, reason });
      } else {
        accepted.push(file);
      }
    });

    if (this.modeValue === 'live') {
      accepted.forEach((file) => this.#liveUploadFile(file));
    } else {
      accepted.forEach((file) => this.addLine(file));
    }

    // Tell the dropzone its work landed so it can reset to placeholder.
    // The `@window->file-dropzone#clear` listener on multi-mode dropzones
    // (Form::DropzoneComponent.multiple?) catches this; live mode dispatches
    // its own from `#liveUploadFile` per-success, buffered mode dispatches
    // once per batch so a rejected-only drop does not clear the zone.
    if (this.modeValue !== 'live' && accepted.length > 0) {
      this.dispatch('attach-state-changed', { detail: { buffered: true, count: accepted.length } });
    }

    if (rejected.length > 0) {
      this.#showFlash(this.#formatRejectionMessage(rejected), 'warning');
    }
  }

  /**
   * @param {File} file
   * @returns {string|null}  null when accepted, French reason string otherwise
   */
  #validateFile(file) {
    const accepted = this.acceptedMimesValue || [];
    if (accepted.length > 0 && !accepted.includes(file.type)) {
      return `Type non supporté (${file.type || 'inconnu'})`;
    }
    const maxBytes = (this.maxFileSizeMbValue || 10) * 1024 * 1024;
    if (file.size > maxBytes) {
      return `Fichier trop volumineux (${(file.size / 1024 / 1024).toFixed(1)} Mo > ${this.maxFileSizeMbValue} Mo)`;
    }
    return null;
  }

  /**
   * Format a single-line French summary of rejected files. Truncate after 3.
   */
  #formatRejectionMessage(rejected) {
    const list = rejected
      .slice(0, 3)
      .map(({ file, reason }) => `${file.name}: ${reason}`)
      .join(' · ');
    const more = rejected.length > 3 ? ` (+${rejected.length - 3} autres)` : '';
    return `Certains fichiers ont été ignorés. ${list}${more}`;
  }

  /**
   * Live-mode portal attach: POST `invoice[invoice_document_id]` to
   * `attachUrlValue`. Hides the card optimistically, then removes it on
   * server confirmation. On HTTP error, restores the card and flashes the
   * reason. The Turbo Stream response is handed to `Turbo.renderStreamMessage`
   * so the new `<tr>` is appended to `#invoice-lines-table` server-side.
   *
   * @param {HTMLElement} card  The pool card element (data-doc-id required).
   */
  async #liveAttachPortalCard(card) {
    const docId = card.dataset.docId;
    if (!docId) return;

    if (!this.hasAttachUrlValue || !this.attachUrlValue) {
      this.#showFlash("URL d'attachement manquante (attachUrlValue).", 'warning');
      return;
    }

    // Idempotency guard. Closes the race window where two near-concurrent
    // calls (e.g. user clicks "+ Attacher" then "Tout attacher" before the
    // first POST resolves) both pass the visible-card check and POST the
    // same doc twice.
    if (card.dataset.attachState !== 'available') return;
    card.dataset.attachState = 'attaching';

    // Optimistic: dim the card immediately so the user sees feedback while the
    // request is in flight. The Turbo broadcast appends the row server-side.
    card.classList.add('opacity-50', 'pointer-events-none');

    try {
      const formData = new FormData();
      formData.append('invoice[invoice_document_id]', docId);

      const response = await this.#postInvoice(formData);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      // Let the @hotwired/turbo global handler process the stream response.
      const body = await response.text();
      if (body) {
        window.Turbo?.renderStreamMessage?.(body);
      }

      // Hide the card from the pending pool — keep it in DOM so a future
      // remove on the corresponding row can re-show it (live-mode restore is
      // a deferred follow-up; we only hide here to stay consistent with
      // buffered mode).
      card.classList.add('hidden');
      card.dataset.attachState = 'attached';
      this.#updatePoolVisibility();
      this.dispatch('attach-state-changed', { detail: { live: true, docId } });
    } catch (error) {
      card.classList.remove('opacity-50', 'pointer-events-none');
      card.dataset.attachState = 'available';
      this.#showFlash(`L'attachement a échoué : ${error.message}`, 'warning');
    }
  }

  /**
   * Live-mode file upload: POST `invoice[file]` to `attachUrlValue`.
   * The Turbo Stream response is handed to `Turbo.renderStreamMessage` so the
   * new `<tr>` is appended to `#invoice-lines-table` server-side. On error,
   * flashes the reason — no client-side row is created.
   *
   * @param {File} file
   */
  async #liveUploadFile(file) {
    if (!this.hasAttachUrlValue || !this.attachUrlValue) {
      this.#showFlash("URL d'attachement manquante (attachUrlValue).", 'warning');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('invoice[file]', file);

      const response = await this.#postInvoice(formData);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const body = await response.text();
      if (body) {
        window.Turbo?.renderStreamMessage?.(body);
      }

      this.dispatch('attach-state-changed', { detail: { live: true, fileName: file.name } });
    } catch (error) {
      this.#showFlash(`L'envoi de ${file.name} a échoué : ${error.message}`, 'warning');
    }
  }

  /**
   * Shared fetch helper for live-mode POSTs. Sends multipart `formData` to
   * `attachUrlValue` with the Turbo Stream Accept header and a CSRF token
   * sourced from either `csrfTokenValue` (explicit value) or the `<meta>`
   * tag (standard Rails layout).
   *
   * @param {FormData} formData
   * @returns {Promise<Response>}
   */
  async #postInvoice(formData) {
    const headers = {
      Accept: 'text/vnd.turbo-stream.html',
    };

    if (this.hasCsrfTokenValue && this.csrfTokenValue) {
      headers['X-CSRF-Token'] = this.csrfTokenValue;
    } else {
      const meta = document.querySelector('meta[name="csrf-token"]');
      if (meta?.content) headers['X-CSRF-Token'] = meta.content;
    }

    return fetch(this.attachUrlValue, {
      method: 'POST',
      body: formData,
      credentials: 'same-origin',
      headers,
    });
  }

  /**
   * Sync the pool badge counter, "Tout attacher" bulk button visibility, and
   * the pool wrapper itself to the current number of remaining cards.
   *
   * Counts only VISIBLE cards (i.e. those without the `hidden` class) so that
   * cards hidden after attach are not counted. The hidden cards stay in the
   * DOM so removeLine can unhide them on row removal.
   */
  #updatePoolVisibility() {
    const remaining = document.querySelectorAll(
      '[data-testid="pending-pool-card"]:not(.hidden)'
    ).length;
    if (this.hasPoolBadgeTarget) {
      this.poolBadgeTarget.textContent = String(remaining);
    }
    if (this.hasBulkAttachButtonTarget) {
      // Tailwind's `.hidden` (display:none) is overridden by `.inline-flex` from
      // ButtonComponent because the latter is declared later in the stylesheet
      // (same specificity, last wins). Use inline style which beats both.
      this.bulkAttachButtonTarget.style.display = remaining < 2 ? 'none' : '';
    }
    if (this.hasPoolWrapperTarget) {
      this.poolWrapperTarget.classList.toggle('hidden', remaining === 0);
    }
  }

  /**
   * Synthesize a row from a portal pool card: clone the template, inject the
   * `invoice_document_id` hidden input, pre-fill custom_name / amount /
   * périmètre from the card's data-*, append to tbody, register a no-File
   * entry in `lineMap`, and remove the source card from the DOM.
   *
   * Idempotent contract: called by both `attachPortal` (single) and
   * `attachAllPortal` (bulk) — the dispatch is emitted by the public action,
   * not here, so bulk-attach only fires once.
   */
  #attachPortalCard(card) {
    if (!this.hasLineTemplateTarget || !this.hasLinesTbodyTarget) return;

    const docId = card.dataset.docId;
    if (!docId) return;

    // Idempotency guard. The function's docstring claims to be idempotent;
    // this enforces it. Covers `attachAllPortal` re-encountering hidden
    // already-attached cards and `#autoAttachFromUrl` landing on a card
    // that was already attached by another path.
    if (card.dataset.attachState !== 'available') return;

    const localId = this.#generateLocalId();
    const index = this.#rowElements().length;

    const fragment = this.lineTemplateTarget.content.cloneNode(true);
    this.#replacePlaceholdersInFragment(fragment, localId, index);

    // Capture the form <tr> BEFORE appendChild — the template emits 4 sibling
    // <tr>s (form row + 2 reconciliation strips + 1 sanity strip), so
    // tbody.lastElementChild would point at the last strip and prefill
    // selectors would silently miss.
    const row = fragment.querySelector('tr[data-testid="invoice-line-form-row"]');
    this.linesTbodyTarget.appendChild(fragment);
    if (!row) return;

    // Tag the row with its source pool card so removeLine can unhide the card
    // when the architect changes their mind. Single source of truth — no
    // separate "is attached" attribute on the card itself.
    row.dataset.fromPortalDocId = String(docId);

    // Mirror the card's persisted-doc URL onto the row so the eye-view button
    // (invoice-line-uploader#previewFile) can open the contractor's PDF
    // without re-uploading. Absent when the doc has no attached file.
    if (card.dataset.fileUrl) {
      row.dataset.fileUrl = card.dataset.fileUrl;
    }

    // The row template doesn't include an invoice_document_id input by default
    // (it's conditional on line_input.invoice_document_id.present?). Synthesize
    // it here so beforeSubmit's hasInvoiceDocumentId check passes and the file
    // path is skipped for this row.
    this.#injectPortalHiddenInputs(row, index, card);

    // Pre-populate visible fields from the card data.
    this.#preFillPortalRowFields(row, card);

    // Register a portal entry — no File. lineMap.size still tracks dirty state
    // for the beforeunload guard.
    this.lineMap.set(localId, { invoiceDocumentId: Number(docId) });
    this.#refreshEmptyState();
    this.updateAmountFieldMode();

    // Set the row's data-row-status so the submit-block guard sees an accurate
    // value (no need to wait for invoice-line-uploader to re-emit — the file
    // path is skipped for portal-attached rows). Priority:
    //   1. The card's pill state (only present while extraction is in flight)
    //   2. Derived from data-doc-amount: empty → 'manual', present → 'extracted'
    const cardPill = card.querySelector('[data-testid="extraction-pill"]');
    const cardState = cardPill?.dataset?.state || (card.dataset.docAmount ? 'extracted' : 'manual');
    row.dataset.rowStatus = cardState;

    // Portal-attached rows show no extraction pill — the contractor's portal
    // already ran extraction; the architect does not need a status signal here.
    // We CLEAR the host (instead of removing it) so the reserved min-w-3 slot
    // stays in the DOM — keeps the Facture-input width identical between
    // portal and manual rows (the host clones in pending-spinner template
    // markup from the server-rendered InvoiceLineFormRowComponent).
    const pillHost = row.querySelector('[data-invoice-line-uploader-target="pill"]');
    if (pillHost) pillHost.replaceChildren();

    // Hide the card from the pool — the same doc can't be attached twice.
    // Keep it in DOM so removeLine can unhide it for an undo-style restore.
    // `data-attach-state` is the LOGICAL dedup signal (read by the entry
    // guard above and by `attachAllPortal`'s selector); `.hidden` remains
    // the VISUAL driver of `#updatePoolVisibility` counting.
    card.classList.add('hidden');
    card.dataset.attachState = 'attached';
    this.#updatePoolVisibility();
  }

  /**
   * Append the hidden `invoice_document_id` input to the cloned row's first
   * cell so the form picks it up on submit.
   */
  #injectPortalHiddenInputs(row, index, card) {
    const invoiceDocInput = document.createElement('input');
    invoiceDocInput.type = 'hidden';
    invoiceDocInput.name = `work_progress_statement[line_inputs][${index}][invoice_document_id]`;
    invoiceDocInput.value = card.dataset.docId;
    const firstCell = row.querySelector('td');
    (firstCell || row).appendChild(invoiceDocInput);
  }

  /**
   * Mirror the card's data-* into the cloned row's visible inputs so the
   * architect sees the portal-extracted defaults without re-typing.
   *
   * `[amount_ht]` is ALWAYS written when `card.dataset.docAmount` is present:
   * the row template (`invoice_line_form_row_component.html.erb:71-80`) emits
   * the HT input for both mono- and multi-VAT contracts, so there is no need
   * to branch on the row shape. The write is guarded by `dataset.userDirty`
   * to avoid clobbering a manually-typed value (same guard used by
   * `#applyExtractionToRow`).
   *
   * Per-rate `[vat_amount_<key>]` inputs are filled from the card's
   * `data-doc-vat-amount-<rate-key>` attributes (underscores in `rateKey`
   * become hyphens for the HTML attribute lookup). VAT inputs are NOT
   * backfilled from the HT total anymore — if the AI extraction produced no
   * per-rate breakdown, VAT inputs stay blank and the architect fills them
   * manually. (Callers expecting an HT-into-VAT fallback need to look at
   * the AI extraction output instead; the previous fallback was semantically
   * wrong because it dumped an HT total into a VAT slot.)
   *
   * Card data attributes (set by PendingPoolCardComponent):
   *   `data-doc-name`              — custom_name pre-fill
   *   `data-doc-amount`            — total HT (from `extracted_amount_ht`)
   *   `data-doc-vat-amount-<key>`  — TVA amount per rate (e.g. `data-doc-vat-amount-10`)
   *   `data-doc-vat-total`         — rate-less total TVA, routed onto the sole
   *                                  active rate when no per-rate breakdown exists
   *   `data-doc-perimetre-id`      — additional_work_id (TS scope)
   */
  #preFillPortalRowFields(row, card) {
    // [custom_name]
    const customNameInput = row.querySelector('input[name$="[custom_name]"]');
    if (customNameInput && card.dataset.docName) {
      customNameInput.value = card.dataset.docName;
    }

    // [amount_ht] — always. The row template always emits this input
    // (invoice_line_form_row_component.html.erb:71-80) for both mono- and
    // multi-VAT contracts. dataset.userDirty mirrors the guard at
    // #applyExtractionToRow (line 1350).
    const amountHtInput = row.querySelector('input[name$="[amount_ht]"]');
    if (amountHtInput && card.dataset.docAmount && amountHtInput.dataset.userDirty !== 'true') {
      amountHtInput.value = card.dataset.docAmount;
      amountHtInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // Per-rate [vat_amount_<key>]. If the AI extracted a total HT but no
    // per-rate breakdown, VAT inputs stay blank — the architect fills them
    // manually (intentional; previous "anyFilled" fallback was wrong because
    // it dumped HT into a VAT slot).
    const perRateInputs = row.querySelectorAll('[data-wps-form-target="vatAmountInput"]');
    perRateInputs.forEach((input) => {
      const rateKey = input.dataset.rateKey;
      if (!rateKey) return;
      // 5_5 → 5-5 (dataset attribute names use hyphens; rateKey uses underscores).
      const value = card.getAttribute(`data-doc-vat-amount-${rateKey.replace(/_/g, '-')}`);
      if (value && input.dataset.userDirty !== 'true') {
        input.value = value;
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    });

    // Rate-less total routing — client mirror of Invoices::ExtractedVatPropagator.
    // When the extractor returned a total VAT with NO per-rate breakdown (the
    // `rate: nil` fallback), the per-rate loop above fills nothing and the VAT
    // input would render blank ("TVA pas extraite"). When the contract has
    // exactly ONE active rate (contractActiveRatesValue, derived server-side
    // from WPS#active_vat_rates — the same gating the propagator uses) we know
    // where that total belongs, so route it onto the sole rate's input.
    const docVatTotal = card.dataset.docVatTotal;
    const activeRates = this.contractActiveRatesValue;
    if (docVatTotal && Array.isArray(activeRates) && activeRates.length === 1) {
      const soleInput = row.querySelector(`input[name$="[vat_amount_${activeRates[0]}]"]`);
      // Fill when the cell is empty OR zero (mirrors the server propagator's
      // `current.blank? || current.zero?` idempotency) and the architect hasn't
      // typed. A freshly-cloned mono-VAT row renders "0.0" here, so a bare
      // `!value` check would wrongly skip routing — never clobber a non-zero
      // value already placed by the per-rate loop or the architect.
      const current = parseFloat(soleInput?.value);
      const fillable = !Number.isFinite(current) || current === 0;
      if (soleInput && fillable && soleInput.dataset.userDirty !== 'true') {
        soleInput.value = docVatTotal;
        soleInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    // [additional_work_id]
    const perimetreSelect = row.querySelector('select[name$="[additional_work_id]"]');
    if (perimetreSelect && card.dataset.docPerimetreId) {
      perimetreSelect.value = card.dataset.docPerimetreId;
    }
  }

  /**
   * Auto-attach the pool card whose `data-doc-id` matches
   * `autoAttachInvoiceDocumentIdValue`. Silent no-op when no card matches
   * (deleted doc, non-portal source, or pool component absent).
   */
  #autoAttachFromUrl() {
    const id = String(this.autoAttachInvoiceDocumentIdValue);
    const card = document.querySelector(`[data-testid='pending-pool-card'][data-doc-id='${id}']`);
    if (!card) return;
    this.#attachPortalCard(card);
    this.#updatePoolVisibility();
    this.dispatch('attach-state-changed', {
      detail: { lineMapSize: this.lineMap.size, source: 'auto-url' },
    });
  }

  /**
   * Observer of `wps-form:row-state-changed` — updates the submit button state
   * when a row transitions out of/into `pending` (which can affect the gate),
   * refreshes the reviewAlert count, and locks/unlocks the row's HT/TVA inputs
   * for the duration of AI extraction. Locking prevents an architect's late
   * keystrokes from racing the AI write; the dataset.userDirty stamp added by
   * `#onUserKeydown` covers the symmetrical case (architect typed before
   * extraction returned).
   *
   * `event.target` is the row element — `invoice-line-uploader#setPillState`
   * dispatches `wps-form:row-state-changed` with `bubbles: true` from the
   * row's controller element.
   *
   * @param {CustomEvent} event
   */
  #onRowStateChanged(event) {
    this.#updateSubmitButton();
    this.#updateReviewAlert();
    this.#syncRowInputDisabled(event.target, event.detail?.state);
    // Bridge: invoice-line-uploader writes extracted_data into the row's
    // hidden input but the row template has no `amountInput` Stimulus target,
    // so its in-uploader auto-fill block is a dead path. Mirror the intended
    // behavior here — fill the row's [amount_ht] input from extracted_data
    // when the row exits `pending` to `completed`. Honors `dataset.userDirty`
    // to avoid clobbering the architect's typing.
    this.#applyExtractionToRow(event.target, event.detail?.state);
  }

  /**
   * Read `extracted_data` from the row's hidden input (set by
   * `invoice-line-uploader#applyAnalysisResult`) and write `amount_ht` into
   * the row's HT input. Only runs on terminal extraction states (anything
   * other than `pending`/empty). Skips when the architect has typed in the
   * field (`dataset.userDirty === 'true'`) or the input already carries a
   * non-zero numeric value (defensive — also covers portal-prefilled rows).
   * Dispatches a synthetic `input` event so `invoice-lines-table` recomputes
   * the footer total → `invoice-lines:total-changed` → `amount-validator`.
   *
   * @param {HTMLElement|EventTarget|null} row
   * @param {string|undefined} state
   */
  #applyExtractionToRow(row, state) {
    if (!row || !state || state === 'pending') return;
    if (typeof row.querySelector !== 'function') return;

    const extractedInput = row.querySelector('input[name$="[extracted_data]"]');
    const raw = extractedInput?.value;
    if (!raw) return;

    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return;
    }

    // [amount_ht] — fill the row's HT input from the extracted total. Guarded
    // INTERNALLY (not via an early return) so a missing/NaN amount_ht no longer
    // short-circuits the per-rate VAT fill below — the AI can return a VAT total
    // with no HT. Skips a userDirty or already-non-zero cell (unchanged behavior).
    const amountHt = parsed?.amount_ht;
    if (typeof amountHt === 'number' && Number.isFinite(amountHt)) {
      const htInput = row.querySelector('input[name$="[amount_ht]"]');
      if (htInput && htInput.dataset.userDirty !== 'true') {
        const currentNumeric = parseFloat(String(htInput.value).replace(',', '.'));
        if (!(Number.isFinite(currentNumeric) && currentNumeric > 0)) {
          htInput.value = amountHt;
          htInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
    }

    // Per-rate [vat_amount_<key>] — the manual-upload mirror of
    // Invoices::ExtractedVatPropagator. Without this, a dropped invoice fills HT
    // but leaves TVA at 0 ("VAT not extracted"); #467 fixed this for portal
    // pool-cards (#preFillPortalRowFields) but never for the manual bridge here.
    this.#fillVatFromBreakdown(row, parsed?.vat_breakdown);
  }

  /**
   * Fill a row's per-rate `[vat_amount_<key>]` inputs from an AI `vat_breakdown`
   * array — the client mirror of `Invoices::ExtractedVatPropagator#build_updates`
   * (the server source of truth). Used by the manual file-upload bridge
   * (`#applyExtractionToRow`); the portal pool-card path keeps its own equivalent
   * in `#preFillPortalRowFields`. Rate → key mapping reuses the existing
   * `#rateToKey` helper.
   *
   * Active-rate filtering is sourced from the DOM: the row template renders a
   * `vatAmountInput` ONLY for the contract's active rates (same source as the
   * server's `active_vat_rates`), so iterating the rendered inputs auto-drops
   * inactive/unknown rates with no dependency on the `contractActiveRates` value
   * (and regardless of `#rateToKey` returning a derived key for an unknown rate).
   * A `rate: null` total is routed onto the sole rate ONLY when the row carries
   * exactly one per-rate input — never guessed on a multi-rate contract.
   *
   * Idempotent: writes a blank/zero, non-`userDirty` cell only, then dispatches a
   * synthetic `input` so TTC / footer / reconciliation recompute. The
   * `event.isTrusted` gate in `#onLineInput` keeps that synthetic event from
   * re-entering the mono-VAT HT→TVA prefill (`#syncMonoVatTvaFromHt`).
   *
   * @param {HTMLElement} row
   * @param {unknown} breakdown  — `extracted_data.vat_breakdown`
   */
  #fillVatFromBreakdown(row, breakdown) {
    if (!Array.isArray(breakdown) || breakdown.length === 0) return;
    if (typeof row.querySelectorAll !== 'function') return;

    // key → input, from the rendered per-rate inputs (= the active rates).
    const inputByKey = {};
    row.querySelectorAll('[data-wps-form-target="vatAmountInput"]').forEach((input) => {
      const key = input.dataset.rateKey;
      if (key) inputByKey[key] = input;
    });
    const renderedKeys = Object.keys(inputByKey);
    if (renderedKeys.length === 0) return;

    breakdown.forEach((entry) => {
      if (!entry || typeof entry !== 'object') return;

      // Skip a blank amount (mirror of the server's `entry['amount_vat'].blank?`);
      // a numeric 0 is kept (corrective 0-VAT line).
      const rawVat = entry.amount_vat;
      if (rawVat === null || rawVat === undefined || rawVat === '') return;
      const amountVat = Number(rawVat);
      if (!Number.isFinite(amountVat)) return;

      let key = this.#rateToKey(entry.rate);
      // Rate-less total → sole rendered rate (mirror of the server's
      // `key ||= active_keys.first if entry['rate'].nil? && active_keys.size == 1`).
      // Gated on the rate being genuinely ABSENT — a present-but-unknown rate
      // (13.5 %) stays dropped, never re-homed.
      if (key === null && entry.rate == null && renderedKeys.length === 1) {
        key = renderedKeys[0];
      }

      const input = key ? inputByKey[key] : null;
      if (!input || input.dataset.userDirty === 'true') return;

      // Fill blank/zero only — never clobber a value the per-rate loop or the
      // architect already placed (a freshly-cloned mono-VAT row renders "0.0").
      const current = parseFloat(String(input.value).replace(',', '.'));
      if (Number.isFinite(current) && current !== 0) return;

      input.value = amountVat.toFixed(2);
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });
  }

  /**
   * Disable/enable the row's HT and per-rate TVA inputs based on extraction
   * state. Only the `pending` state locks; all other states (`completed`,
   * `failed`, `manual`, etc.) re-enable. No-op when `row` or `state` is
   * missing.
   *
   * @param {HTMLElement|EventTarget|null} row
   * @param {string|undefined} state
   */
  #syncRowInputDisabled(row, state) {
    if (!row || !state) return;
    if (typeof row.querySelectorAll !== 'function') return;
    const shouldDisable = state === 'pending';
    row
      .querySelectorAll('input[name$="[amount_ht]"], input[name*="vat_amount_"]')
      .forEach((input) => {
        input.disabled = shouldDisable;
      });
  }

  /**
   * Single-delegated keydown handler — stamps `dataset.userDirty='true'` on
   * the first keystroke targeting any HT or TVA input. Coordinated with
   * `invoice-line-uploader#applyAnalysisResult`, which skips writing to
   * inputs already flagged userDirty so a late AI extraction can't clobber
   * an architect's edit.
   *
   * @param {KeyboardEvent} event
   */
  #onUserKeydown(event) {
    const t = event.target;
    if (!t || typeof t.matches !== 'function') return;
    if (!t.matches('input[name$="[amount_ht]"], input[name*="vat_amount_"]')) return;
    if (t.dataset.userDirty === 'true') return;
    t.dataset.userDirty = 'true';
  }

  /**
   * @returns {HTMLElement|undefined}  the first row whose `data-row-status`
   *   is `'pending'` (AI extraction in flight), or undefined.
   */
  #firstPendingRow() {
    return this.#rowElements().find((row) => row.dataset.rowStatus === 'pending');
  }

  /**
   * Shift focus into a row — prefer the first focusable child; fall back to
   * the row itself with a temporary `tabindex="-1"`.
   */
  #focusRow(row) {
    // Skip hidden inputs (analysis_status, extracted_data, invoice_document_id)
    // — focusing a display-none element is a silent no-op. Prefer the first
    // VISIBLE focusable; fall back to the row itself with a temporary
    // tabindex="-1" so :focus / :focus-within match.
    const focusable = row.querySelector(
      'button:not(.hidden):not([hidden]), input:not([type="hidden"]):not(.hidden), select:not(.hidden), [tabindex]:not(.hidden)'
    );
    if (focusable) {
      focusable.focus({ preventScroll: false });
      return;
    }
    if (!row.hasAttribute('tabindex')) {
      row.setAttribute('tabindex', '-1');
    }
    row.focus({ preventScroll: false });
  }

  /**
   * Render (or update) a small inline flash inside the panel. Not a full
   * toast system — single container, replaced text on each call. Inserted
   * immediately above the lines table for visibility.
   */
  #showFlash(message, level = 'info') {
    const flashClass =
      level === 'warning'
        ? 'bg-warning-50 border border-warning-200 text-warning-800'
        : 'bg-info-50 border border-info-200 text-info-800';

    let container = this.element.querySelector('[data-wps-form-flash]');
    if (!container) {
      container = document.createElement('div');
      container.dataset.wpsFormFlash = 'true';
      container.setAttribute('role', 'alert');
      container.setAttribute('aria-live', 'polite');
      container.className = `mt-2 mb-2 rounded-md p-3 text-sm ${flashClass}`;
      const anchor = this.hasLinesTableTarget
        ? this.linesTableTarget.parentElement
        : this.element.firstElementChild;
      anchor?.parentElement?.insertBefore(container, anchor);
    } else {
      container.className = `mt-2 mb-2 rounded-md p-3 text-sm ${flashClass}`;
    }
    container.textContent = message;
  }

  #rowElements() {
    if (!this.hasLinesTbodyTarget) return [];
    return Array.from(this.linesTbodyTarget.querySelectorAll('[data-local-id]'));
  }

  #refreshEmptyState() {
    const hasRows = this.#rowElements().length > 0;
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.toggle('hidden', hasRows);
    }
    if (this.hasLinesTableTarget) {
      this.linesTableTarget.classList.toggle('hidden', !hasRows);
    }
  }

  #generateLocalId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    // Fallback for environments without crypto.randomUUID (very old browsers,
    // some testing harnesses). Sufficient uniqueness for client-side keys.
    return `line-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  /**
   * Replace literal `__LOCAL_ID__` and `__INDEX__` tokens in attribute values
   * across a freshly-cloned <template> fragment.
   */
  #replacePlaceholdersInFragment(fragment, localId, index) {
    const treeWalker = document.createTreeWalker(fragment, NodeFilter.SHOW_ELEMENT);
    let node = treeWalker.currentNode === fragment ? treeWalker.nextNode() : treeWalker.currentNode;
    while (node) {
      Array.from(node.attributes || []).forEach((attr) => {
        if (attr.value.includes('__LOCAL_ID__') || attr.value.includes('__INDEX__')) {
          const replaced = attr.value
            .replaceAll('__LOCAL_ID__', localId)
            .replaceAll('__INDEX__', String(index));
          node.setAttribute(attr.name, replaced);
        }
      });
      node = treeWalker.nextNode();
    }
  }

  /**
   * Rewrite all `name="work_progress_statement[line_inputs][N][...]"` so N
   * matches the row's current position in tbody.
   */
  #applyIndexToRow(row, index) {
    const NAME_RE = /work_progress_statement\[line_inputs\]\[\d+\]\[([^\]]+)\]/g;
    row.querySelectorAll('[name]').forEach((el) => {
      const next = el.getAttribute('name').replace(NAME_RE, (_, key) => {
        return `work_progress_statement[line_inputs][${index}][${key}]`;
      });
      el.setAttribute('name', next);
    });
  }

  /**
   * Ensure the row contains a hidden file input populated via DataTransfer.
   * Creates the input lazily on first submit; reuses it on subsequent submits
   * (e.g. validation failure → user fixes a field → resubmits).
   */
  #ensureFileInput(row, index, file) {
    const name = `work_progress_statement[line_inputs][${index}][file]`;
    let input = row.querySelector(`input[type="file"][data-wps-form-managed="true"]`);

    if (!input) {
      input = document.createElement('input');
      input.type = 'file';
      input.hidden = true;
      input.dataset.wpsFormManaged = 'true';
      // Append at the start of the first cell so it rides the form on submit.
      const firstCell = row.querySelector('td');
      (firstCell || row).appendChild(input);
    }

    input.setAttribute('name', name);

    const dt = new DataTransfer();
    dt.items.add(file);
    input.files = dt.files;
  }

  /**
   * Snapshot every form input's value at connect-time so we can later detect
   * whether the architect has edited the form (excluding our managed rows,
   * which are tracked via `lineMap.size`).
   */
  #snapshotForm() {
    const form =
      this.element.tagName === 'FORM' ? this.element : this.element.querySelector('form');
    if (!form) return new Map();

    const snapshot = new Map();
    form.querySelectorAll('input, select, textarea').forEach((el) => {
      if (el.type === 'file') return;
      const key = `${el.name || el.id || ''}::${snapshot.size}`;
      const value =
        el.type === 'checkbox' || el.type === 'radio' ? String(el.checked) : el.value || '';
      snapshot.set(key, value);
    });
    return snapshot;
  }

  #formIsDirty() {
    const current = this.#snapshotForm();
    if (current.size !== this.initialFormSnapshot.size) return true;
    for (const [key, value] of current) {
      if (this.initialFormSnapshot.get(key) !== value) return true;
    }
    return false;
  }

  // ── Reconciliation helpers ────────────────────────────────────────────────

  /**
   * Find a data row by its `data-local-id` attribute, scoped to the controller
   * element. Returns null when not found (e.g. live-mode rows whose localId is
   * not in lineMap).
   *
   * @param {string} localId
   * @returns {HTMLElement|null}
   */
  #rowByLocalId(localId) {
    return this.element.querySelector(`[data-local-id="${CSS.escape(localId)}"]`);
  }

  /**
   * Compute the implied Total HT for a row.
   *
   * Multi-VAT rows carry per-rate TVA inputs (`data-wps-form-target="vatAmountInput"`
   * with `data-rate-key`). The HT for each rate is back-calculated as
   * `round(tva × 100 / rate_pct, 2)` and summed; the per-rate `.round(2)` keeps
   * IEEE 754 sub-centime drift out of the displayed HT (the sum feeds
   * `#updateLineTotalHtSpan` and `recalcTotal → amountInput.value`). NOT
   * server-lockstep — `Invoice#vat_amount_coherence` was deleted in PR #264.
   *
   * Mono-VAT rows carry a single `amount_ht` input; that value is returned directly.
   *
   * @param {HTMLElement} row
   * @returns {number}
   */
  #computeLineTotalHt(row) {
    const vatInputs = Array.from(row.querySelectorAll('[data-wps-form-target="vatAmountInput"]'));
    if (vatInputs.length > 0) {
      return vatInputs.reduce((sum, input) => {
        const tva = parseFloat(input.value) || 0;
        const rateKey = input.dataset.rateKey || '';
        const ratePct = parseFloat(rateKey.replace('_', '.')) || 0;
        if (ratePct <= 0) return sum;
        const impliedHtForRate = Math.round(((tva * 100) / ratePct) * 100) / 100;
        return sum + impliedHtForRate;
      }, 0);
    }

    // Mono-VAT fallback: use the single amount_ht input.
    const amountHtInput = row.querySelector('input[name$="[amount_ht]"]');
    return parseFloat(amountHtInput?.value) || 0;
  }

  // Alias kept for reconciliation/recalc callers (#recomputeReconciliation, recalcTotal).
  #sumPerRateInputs(row) {
    return this.#computeLineTotalHt(row);
  }

  /**
   * Update the `lineTotalHt` span inside a row with the computed Total HT.
   * No-op when the span is absent (mono-VAT rows don't render it).
   *
   * @param {HTMLElement} row
   */
  #updateLineTotalHtSpan(row) {
    const span = row.querySelector('[data-wps-form-target="lineTotalHt"]');
    if (!span) return;
    const totalHt = this.#computeLineTotalHt(row);
    const next = `${this.#formatCurrency(totalHt)}`;
    if (span.textContent === next) return; // skip: no real change → no MO churn
    span.textContent = next;
  }

  /**
   * Real-time mirror of `Invoice#vat_total_within_realistic_range` (server-side
   * sanity guard shipped in 07b6e69d). Reveals the dedicated `vat_sanity` strip
   * `<tr>` when `Σ vat_amount_<rate> > amount_ht × 0.30`.
   *
   * Boundary mirrors the server `<=` exactly: the JS test is `>` (strict), so
   * the inclusive server case (`Σ vat == HT × 0.30`) stays valid client-side.
   *
   * HT source: prefers the row's `[amount_ht]` input; falls back to the
   * `lineTotalHt` span (multi-VAT buffered rows where HT is back-calculated)
   * and finally to 0 (no input, no span — strip stays hidden because
   * `vat > 0 * 0.30 = 0` only fires when vat > 0).
   *
   * Safe on every keystroke because the 30 % threshold sits 50 % above the
   * highest legal French VAT rate (20 %), so legitimate values never trip it.
   *
   * @param {string} localId
   */
  #recomputeVatSanity(localId) {
    const row = this.#rowByLocalId(localId);
    if (!row) return;

    const htInput = row.querySelector('input[name$="[amount_ht]"]');
    let ht = parseFloat(htInput?.value);
    if (!Number.isFinite(ht)) {
      const span = row.querySelector('[data-wps-form-target="lineTotalHt"]');
      ht = span ? this.#parseLocaleNumber(span.textContent) : 0;
    }

    let vat = 0;
    row.querySelectorAll('input[name*="vat_amount_"]').forEach((i) => {
      vat += parseFloat(i.value) || 0;
    });

    // Compare absolute magnitudes on both sides — mirrors the server-side
    // `.abs` guard in `Invoice#vat_total_within_realistic_range`. This prevents
    // a false "TVA > 30%" warning on coherent avoir lines (negative HT + negative
    // VAT) while still flagging a magnitude-transposition typo.
    const violates = Math.abs(vat) > Math.abs(ht) * 0.3;
    this.#setStripVisible(localId, 'vat_sanity', violates);

    if (violates) {
      const strip = this.element.querySelector(this.#stripSelector(localId, 'vat_sanity'));
      strip
        ?.querySelector('[data-vat-sanity-target="ht"]')
        ?.replaceChildren(document.createTextNode(this.#formatCurrency(ht)));
      strip
        ?.querySelector('[data-vat-sanity-target="vat"]')
        ?.replaceChildren(document.createTextNode(this.#formatCurrency(vat)));
    }
  }

  /**
   * Advisory per-line VAT coherence check (NON-BLOCKING — an aid, never a gate).
   *
   * Flags when the entered VAT is implausible vs the contract's active rate(s)
   * applied to HT, so the architect double-checks:
   *   - mono-rate: expected VAT = round(HT × taux, 2); flag when
   *     |Σvat − expected| > max(0,02 €, expected × 0,5 %). Fires on an empty/zero
   *     VAT too (HT filled, VAT missing) — the post-extraction missing-VAT nudge.
   *   - multi-rate: each entered TVA implies its base (TVA ÷ taux), and those
   *     bases must sum to the declared HT — Σ(TVA_i / taux_i) ≈ HT. Exact; fires
   *     as soon as ≥1 cell is entered (Σvat > 0; empty cells count as 0) — like
   *     mono, so a single-rate-on-multi line that doesn't reconcile is flagged
   *     (a transient warning mid-split is non-blocking and self-clears). FIXED
   *     0,50 € tolerance (the inverse round-trip's rounding noise is bounded in
   *     absolute terms, so it must not scale with HT). Message picks the most
   *     actionable framing: exactly ONE empty cell → the unique missing VAT is
   *     derived from the remaining HT (« TVA X % attendue : Y »); all cells
   *     filled (gap unattributable) or ≥2 empty (underdetermined) → line-level
   *     « cohérente avec un HT de Z ».
   *     This is the ADR-009-deleted inverse round-trip, reintroduced as a
   *     NON-BLOCKING advisory only (ADR-009 addendum 2026-06-18).
   *
   * Suppressed when the RED 30 % sanity strip owns the line (`vat > ht × 0,30`)
   * so the two never stack; skipped on a blank line (HT 0) and franchise (no
   * active rate) lines — but an HT-filled line with a missing VAT now nudges. Avoir-safe (mono compares `.abs` magnitudes; multi compares the
   * signed implied HT to the signed declared HT). HT is read from the HT INPUT
   * (stored decimal), never the VAT cell. Re-renders only on verdict change (the
   * polite live region must not re-announce on every keystroke).
   *
   * @param {string} localId
   */
  #recomputeVatCoherence(localId) {
    const row = this.#rowByLocalId(localId);
    if (!row) return;

    const htInput = row.querySelector('input[name$="[amount_ht]"]');
    let htRaw = parseFloat(htInput?.value);
    if (!Number.isFinite(htRaw)) {
      const span = row.querySelector('[data-wps-form-target="lineTotalHt"]');
      htRaw = span ? this.#parseLocaleNumber(span.textContent) : 0;
    }
    const ht = Math.abs(htRaw);

    // Per-rate VAT inputs paired with their rate %, for the implied-HT identity.
    const vatEntries = Array.from(row.querySelectorAll('[data-wps-form-target="vatAmountInput"]'))
      .map((i) => ({ vat: parseFloat(i.value), pct: WPS_FORM_RATE_BY_KEY[i.dataset.rateKey] }))
      .filter((e) => typeof e.pct === 'number');
    const vat = Math.abs(vatEntries.reduce((s, e) => s + (Number.isFinite(e.vat) ? e.vat : 0), 0));

    let show = false;
    let expectedText = '';
    // Only consider showing OUTSIDE the 30 % red-sanity band (it owns that case).
    if (ht > 0 && vatEntries.length > 0 && vat <= ht * 0.3) {
      if (vatEntries.length === 1) {
        // Mono-rate: expected VAT = HT × taux; the single cell is unambiguous.
        // An empty/zero VAT on a non-zero HT line is also implausible, so it
        // fires too (NO `vat > 0` guard) — the missing-VAT nudge the architect
        // needs after a failed extraction. Mirrors the multi-rate branch, which
        // already fires on empty cells. The outer `ht > 0` guard keeps a blank
        // line quiet, and the tolerance keeps negligible-HT noise quiet.
        const pct = vatEntries[0].pct / 100;
        const expected = Math.round(ht * pct * 100) / 100;
        const tolerance = Math.max(0.02, expected * 0.005);
        show = Math.abs(vat - expected) > tolerance;
        if (show) expectedText = `attendu ≈ ${this.#formatCurrency(expected)}`;
      } else {
        // Multi-rate: each entered TVA implies its base (TVA ÷ taux); those
        // implied bases must sum to the declared HT — Σ(TVA_i / taux_i) ≈ HT.
        // Fires as soon as ≥1 cell is entered (vat > 0). FIXED 0,50 € tolerance:
        // the inverse round-trip's rounding noise is bounded in ABSOLUTE terms
        // (~0,17 € worst case, 3 rates), so it must NOT scale with HT. Advisory
        // only — reintroduces the ADR-009-deleted inverse round-trip as a
        // non-blocking aid (see ADR-009 addendum 2026-06-18).
        //
        // Message picks the most actionable framing available:
        //   - exactly ONE rate cell still empty → derive its expected VAT from
        //     the remaining HT (the unknown is unique): "TVA X % attendue : Y".
        //   - all cells filled (can't attribute the gap to one) OR ≥2 empty
        //     (underdetermined) → line-level "cohérente avec un HT de Z".
        const TOL = 0.5;
        const filled = vatEntries.filter((e) => Number.isFinite(e.vat) && e.vat !== 0);
        const empty = vatEntries.filter((e) => !Number.isFinite(e.vat) || e.vat === 0);
        const filledBase = filled.reduce((s, e) => s + (e.vat * 100) / e.pct, 0);

        if (empty.length === 1) {
          const remainingHt = htRaw - filledBase;
          if (remainingHt > TOL) {
            // The lone empty rate should carry the VAT for the leftover HT.
            const expectedVat = (remainingHt * empty[0].pct) / 100;
            const rateLabel = String(empty[0].pct).replace('.', ',');
            show = true;
            expectedText = `TVA ${rateLabel} % attendue : ${this.#formatCurrency(expectedVat)}`;
          } else if (remainingHt < -TOL) {
            // Filled TVAs already over-cover the HT → no positive "missing"
            // value to suggest; fall back to the line-level framing.
            show = true;
            expectedText = `cohérente avec un HT de ${this.#formatCurrency(Math.abs(filledBase))}`;
          }
        } else if (Math.abs(filledBase - htRaw) > TOL) {
          show = true;
          expectedText = `cohérente avec un HT de ${this.#formatCurrency(Math.abs(filledBase))}`;
        }
      }
    }

    // Verdict-change guard — only touch the DOM when the shown verdict or the
    // displayed figures actually change, so the polite region stays quiet.
    const sig = show
      ? `1|${this.#formatCurrency(ht)}|${this.#formatCurrency(vat)}|${expectedText}`
      : '0';
    if (row.dataset.coherenceSig === sig) return;
    row.dataset.coherenceSig = sig;

    // This advisory is deliberately NOT rolled up into #updateReviewAlert (the
    // top banner) — it stays an inline-only nudge. See #updateReviewAlert.
    this.#setStripVisible(localId, 'vat_coherence', show);
    if (!show) return;

    const strip = this.element.querySelector(this.#stripSelector(localId, 'vat_coherence'));
    strip
      ?.querySelector('[data-vat-coherence-target="ht"]')
      ?.replaceChildren(document.createTextNode(this.#formatCurrency(ht)));
    strip
      ?.querySelector('[data-vat-coherence-target="vat"]')
      ?.replaceChildren(document.createTextNode(this.#formatCurrency(vat)));
    strip
      ?.querySelector('[data-vat-coherence-target="expected"]')
      ?.replaceChildren(document.createTextNode(expectedText));
  }

  /**
   * Core reconciliation logic for a single line.
   *
   * Reads the current state of per-rate inputs and compares against the
   * AI-extracted reference values stored in `lineMap`. Toggles the visibility
   * of the two strip `<tr>`s (sum and rate) and updates the top-of-form alert
   * + submit button state.
   *
   * Sum mismatch: `|referenceAmountHt − sumHt| > 0.01` AND
   *   `referenceAmountHt > 0` AND `referenceVatBreakdown.length > 1`.
   *   (Single-entry breakdowns cannot produce a meaningful sum mismatch.)
   *
   *   NOTE: `0.01` here is intentionally tighter than the `0.02` used in
   *   `#updateCoherenceWarning`. This check compares the AI-extracted
   *   `referenceAmountHt` (a raw decimal, never produced by `round(HT × rate)`)
   *   against `Σ vat × 100 / rate`. Only ONE side has been through the
   *   rounding round-trip, so no extra drift budget is needed — `0.01`
   *   covers ordinary float-addition noise.
   *
   * Rate mismatch: any rate in `referenceVatBreakdown` that is not in
   *   `contractActiveRatesValue` AND `rateMismatchDismissed` is false.
   *   An empty `contractActiveRatesValue` means "all rates accepted" —
   *   no rate-mismatch warning is shown.
   *
   * @param {string} localId
   */
  #recomputeReconciliation(localId) {
    const entry = this.lineMap.get(localId);

    // When there is no lineMap entry (live-mode rows not tracked by lineMap),
    // we have no reference to compare against — skip.
    if (!entry) return;

    const referenceAmountHt = entry.referenceAmountHt;
    const breakdown = entry.referenceVatBreakdown || [];

    const row = this.#rowByLocalId(localId);
    if (!row) return;

    // ── Sum mismatch ─────────────────────────────────────────────────────────
    let hasSumMismatch = false;
    if (
      typeof referenceAmountHt === 'number' &&
      Number.isFinite(referenceAmountHt) &&
      referenceAmountHt > 0 &&
      breakdown.length > 1
    ) {
      const sumHt = this.#sumPerRateInputs(row);
      hasSumMismatch = Math.abs(referenceAmountHt - sumHt) > 0.01;
    }

    // ── Rate mismatch ─────────────────────────────────────────────────────────
    let hasRateMismatch = false;
    if (!entry.rateMismatchDismissed && breakdown.length > 0) {
      const contractRates = this.contractActiveRatesValue;
      // Only run rate checks when the contract has declared active rates.
      if (contractRates.length > 0) {
        hasRateMismatch = breakdown.some((entry_) => {
          const rate = entry_.rate;
          if (rate == null) return false;
          return !contractRates.includes(String(rate));
        });
      }
    }

    // ── Apply visibility to row and strips ────────────────────────────────────
    this.#setStripVisible(localId, 'sum', hasSumMismatch);
    this.#setStripVisible(localId, 'rate', hasRateMismatch);

    // Tint the data row when any warning is active.
    const hasAnyWarning = hasSumMismatch || hasRateMismatch;
    row.classList.toggle('[&>td]:bg-warning-50', hasAnyWarning);
    // Also set a data attribute for server-side consumers and CSS targeting.
    if (hasAnyWarning) {
      row.dataset.reconciliationStatus =
        hasSumMismatch && hasRateMismatch
          ? 'both'
          : hasSumMismatch
            ? 'sum_mismatch'
            : 'rate_mismatch';
    } else {
      delete row.dataset.reconciliationStatus;
    }

    this.#updateReviewAlert();
    this.#updateSubmitButton();
  }

  /**
   * Build the per-line strip selector — the single source of truth shared by the
   * show/hide readers and the removeLine teardown. `kind` null matches every
   * warning kind for the line (sum, rate, vat_sanity, vat_coherence).
   *
   * @param {string} localId
   * @param {string|null} kind
   * @returns {string}
   */
  #stripSelector(localId, kind = null) {
    const kindSel = kind ? `[data-warning-kind="${kind}"]` : '';
    return `tr[data-testid="row-strip-warning"]${kindSel}[data-line-id="${CSS.escape(localId)}"]`;
  }

  /**
   * Remove every warning strip `<tr>` belonging to a line. The strips are
   * SIBLINGS of the line row (rendered after it, keyed by `data-line-id`), not
   * children — so `row.remove()` alone would orphan any visible strip. Called
   * from removeLine (buffered mode); live/edit mode re-renders the whole
   * `invoice-lines-table` server-side, so its strips never orphan.
   *
   * @param {string} localId
   */
  #removeStripsFor(localId) {
    this.element.querySelectorAll(this.#stripSelector(localId)).forEach((strip) => strip.remove());
  }

  /**
   * Show or hide a row-strip `<tr>` for a given line and warning kind.
   * Locates the strip via `data-testid="row-strip-warning"` + `data-warning-kind`
   * + `data-line-id`. Both strips start with `hidden` (server-rendered).
   *
   * When revealing a strip (`visible === true`), populates the
   * `[data-strip-target="detail"]` span (and, for :rate kind, the
   * `[data-strip-target="summary"]` span) with computed values so the
   * architect sees real numbers instead of the `…` template-mode placeholders.
   *
   * @param {string} localId
   * @param {'sum'|'rate'} kind
   * @param {boolean} visible
   */
  #setStripVisible(localId, kind, visible) {
    const strip = this.element.querySelector(this.#stripSelector(localId, kind));
    if (!strip) return;

    if (visible) {
      strip.removeAttribute('hidden');
      this.#populateStripCopy(strip, localId, kind);
    } else {
      strip.setAttribute('hidden', '');
    }
  }

  /**
   * Write computed values into a strip's `[data-strip-target="detail"]` span
   * (and `[data-strip-target="summary"]` for :rate kind). Called only when
   * the strip is being revealed so that placeholder `…` copy is never shown.
   *
   * @param {HTMLElement} strip   — the `<tr>` element for the strip
   * @param {string} localId
   * @param {'sum'|'rate'} kind
   */
  #populateStripCopy(strip, localId, kind) {
    const detail = strip.querySelector('[data-strip-target="detail"]');
    if (!detail) return;

    const entry = this.lineMap.get(localId);

    if (kind === 'sum') {
      const row = this.#rowByLocalId(localId);
      const referenceAmountHt = entry?.referenceAmountHt ?? 0;
      const sommeHt = row ? this.#sumPerRateInputs(row) : 0;
      const ecart = Math.abs(referenceAmountHt - sommeHt);
      detail.textContent = `somme HT = ${this.#formatCurrency(sommeHt)} · Total IA = ${this.#formatCurrency(referenceAmountHt)} (écart ${this.#formatCurrency(ecart)})`;
    } else if (kind === 'rate') {
      const breakdown = entry?.referenceVatBreakdown ?? [];
      const contractRates = this.contractActiveRatesValue;

      const offendingKeys = breakdown
        .map((e) => this.#rateToKey(e.rate))
        .filter((k) => k !== null && !contractRates.includes(k));

      const offendingFormatted = offendingKeys.map((k) => `${k === '5_5' ? '5,5' : k}%`).join(', ');

      const activeFormatted = contractRates.map((k) => `${k === '5_5' ? '5,5' : k}%`).join(', ');

      detail.textContent = `Le marché utilise ${activeFormatted}. La facture indique ${offendingFormatted}.`;

      // Also update the summary span for :rate — the server renders
      // "Taux … absent du marché." as a placeholder; replace the first
      // offending rate into it so screen readers and sighted users both
      // see a concrete value.
      const summary = strip.querySelector('[data-strip-target="summary"]');
      if (summary && offendingFormatted) {
        summary.textContent = `Taux ${offendingFormatted} absent du marché.`;
      }
    }
  }

  /**
   * Format a currency amount using the French locale (space thousands separator,
   * comma decimal separator, Euro symbol suffix).
   *
   * @param {number} amount
   * @returns {string}  e.g. "1 234,56 €"
   */
  #formatCurrency(amount) {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
    }).format(amount);
  }

  /**
   * Map a numeric VAT rate to its contract key string, mirroring the Ruby
   * `Invoices::ReconciliationStatus#rate_to_key` method. Uses epsilon
   * comparisons to avoid IEEE 754 rounding issues.
   *
   * Examples: 5.5 → '5_5', 10.0 → '10', 20.0 → '20', 8.5 → '8_5'
   *
   * @param {number|null} rate
   * @returns {string|null}
   */
  #rateToKey(rate) {
    if (rate == null) return null;
    if (Math.abs(rate - 5.5) < 0.01) return '5_5';
    if (Math.abs(rate - 10.0) < 0.01) return '10';
    if (Math.abs(rate - 20.0) < 0.01) return '20';
    return String(rate).replace('.', '_');
  }

  /**
   * Count visible (non-hidden) row-strip-warning `<tr>`s across the form and
   * update the top-of-form reviewAlert's visibility and count label.
   *
   * Excludes `vat_coherence`: that advisory is a soft, non-blocking inline-only
   * nudge — rolling it up into the top banner duplicated the inline strip and
   * read as noise. The banner stays reserved for the stronger signals (the red
   * 30 % sanity guard + AI reconciliation sum/rate). Alert shown when count > 0.
   */
  #updateReviewAlert() {
    if (!this.hasReviewAlertTarget) return;

    const visibleStrips = this.element.querySelectorAll(
      'tr[data-testid="row-strip-warning"]:not([hidden]):not([data-warning-kind="vat_coherence"])'
    );
    const count = visibleStrips.length;

    if (count > 0) {
      this.reviewAlertTarget.removeAttribute('hidden');
      if (this.hasReviewAlertCountTarget) {
        this.reviewAlertCountTarget.textContent = String(count);
      }
    } else {
      this.reviewAlertTarget.setAttribute('hidden', '');
    }
  }

  /**
   * Aggregate disable reasons for the submit button. Priority order (first hit
   * wins the title tooltip):
   *
   *   1. AI extraction in flight on any row
   *   2. Missing `statement_type` selection
   *   3. Contract-total overrun (read from amount-validator)
   *   4. Unresolved sum mismatch on any row
   *
   * When any reason hits, the button is disabled with a French title; when
   * none hit, it's re-enabled and the disabled styling is cleared. Rate
   * mismatches deliberately do NOT block submit.
   */
  #updateSubmitButton() {
    if (!this.hasSubmitButtonTarget) return;

    const button = this.submitButtonTarget;
    let title = null;

    if (this.#firstPendingRow()) {
      title = 'Une extraction IA est en cours';
    } else if (this.#missingStatementType()) {
      title = this.selectTypeLabelValue || 'Sélectionnez un type de situation';
    } else if (this.#amountValidatorOverrun()) {
      title = 'Le montant HT dépasse le total du contrat';
    } else if (this.#hasUnresolvedSumMismatch()) {
      title = 'Vérifiez les lignes en surbrillance';
    }

    const blocked = title !== null;
    button.disabled = blocked;
    button.setAttribute('aria-disabled', String(blocked));

    if (blocked) {
      button.classList.add('opacity-50', 'cursor-not-allowed');
      button.title = title;
    } else {
      button.classList.remove('opacity-50', 'cursor-not-allowed');
      button.title = '';
    }
  }

  /**
   * Returns true when the form has a `statementTypeSelect` target, the select
   * is enabled (i.e. not server-locked on /edit when the type is committed),
   * and no option is selected. Defensive for legacy templates without the
   * target — they return false (no blocker contributed).
   *
   * @returns {boolean}
   */
  #missingStatementType() {
    if (!this.hasStatementTypeSelectTarget) return false;
    const select = this.statementTypeSelectTarget;
    if (select.disabled === true) return false;
    return select.value === '';
  }

  /**
   * Returns true when the sibling `amount-validator` controller is currently
   * showing the overrun errorBox. Looked up on demand so the call survives the
   * Stimulus connect order.
   *
   * @returns {boolean}
   */
  #amountValidatorOverrun() {
    const validator = this.application.getControllerForElementAndIdentifier(
      this.element,
      'amount-validator'
    );
    return validator?.isOverrun?.() === true;
  }

  /**
   * Returns true when any row has a visible (non-hidden) sum-mismatch strip.
   * Used by the submit gate and `#updateSubmitButton`.
   *
   * @returns {boolean}
   */
  #hasUnresolvedSumMismatch() {
    return !!this.element.querySelector(
      'tr[data-testid="row-strip-warning"][data-warning-kind="sum"]:not([hidden])'
    );
  }

  /**
   * Inject (or update) a hidden input for `rate_mismatch_dismissed_at` on a
   * buffered-mode row. The input name follows the same
   * `work_progress_statement[line_inputs][N][…]` convention so the Rails form
   * object picks it up on submit.
   *
   * @param {HTMLElement|null} row
   * @param {string} isoTimestamp
   */
  #injectRateMismatchDismissedInput(row, isoTimestamp) {
    if (!row) return;

    let input = row.querySelector('input[data-wps-form-rate-mismatch-dismissed="true"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.dataset.wpsFormRateMismatchDismissed = 'true';
      // Name will be rewritten by #applyIndexToRow on submit — use a placeholder
      // index that survives reindexRows if it hasn't run yet.
      const existingName = row.querySelector('input[name]')?.getAttribute('name') || '';
      const indexMatch = existingName.match(/\[line_inputs\]\[(\d+)\]/);
      const index = indexMatch ? indexMatch[1] : '0';
      input.name = `work_progress_statement[line_inputs][${index}][rate_mismatch_dismissed_at]`;
      const firstCell = row.querySelector('td');
      (firstCell || row).appendChild(input);
    }
    input.value = isoTimestamp;
  }

  /**
   * PATCH the invoice's `rate_mismatch_dismissed_at` field in live mode
   * (/edit). The row must carry `data-invoice-id` (server-rendered for live rows).
   * Failure is logged but does NOT surface a flash — the UI has already
   * updated optimistically.
   *
   * @param {HTMLElement|null} row
   * @param {string} isoTimestamp
   */
  async #patchRateMismatchDismissed(row, isoTimestamp) {
    if (!row) return;

    const invoiceId = row.dataset.invoiceId;
    if (!invoiceId) return;

    const csrfToken =
      (this.hasCsrfTokenValue && this.csrfTokenValue) ||
      document.querySelector('meta[name="csrf-token"]')?.content ||
      '';

    // Build the PATCH URL from the current page path, which is structured as
    // /projects/:pid/contracts/:cid/work_progress_statements/:wps_id/…
    // The invoice resource is nested: /…/invoices/:invoice_id
    const wpsPath = window.location.pathname;
    const patchUrl = `${wpsPath}/invoices/${invoiceId}`;

    try {
      await fetch(patchUrl, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          Accept: 'application/json',
        },
        body: JSON.stringify({
          invoice: { rate_mismatch_dismissed_at: isoTimestamp },
        }),
      });
    } catch (error) {
      // Optimistic UI already updated — log and continue.
      console.error('[wps-form] Failed to persist rate_mismatch_dismissed_at:', error);
    }
  }
}
